import { profiles, ADMIN_SISTEMA } from "./profiles";

/**
 * Matriz de permisos del módulo de Subcontratación (sección 6 del documento de reglas
 * de negocio SGR_RN_SUB_ControlSubcontratos).
 *
 * Se centraliza acá en lugar de repartir comparaciones de perfil por los componentes,
 * para que la matriz sea legible de un vistazo y se pueda auditar contra el documento.
 *
 * OJO: este control es solo de interfaz. El backend no valida perfiles, así que estos
 * permisos ocultan acciones pero no las impiden a quien llame la API directamente.
 */

const DIRECTOR = profiles[0].name;      // Director de proyectos
const WORKS_CONTROL = profiles[1].name; // Control de Obras
const MANAGER = profiles[2].name;       // Gerente
const VIEWER = profiles[3].name;        // Visualizador
const ADMIN = profiles[4].name;         // Administrativo

export interface SubcontractPermissions {
    /** Ver el listado del mantenedor. */
    canView: boolean;
    /** Crear empresas subcontratistas. */
    canCreate: boolean;
    /** Editar razón social y RUT. */
    canEditBasicData: boolean;
    /** Editar las especialidades asignadas. */
    canEditSections: boolean;
    /** Asignar o desvincular proyectos. */
    canEditProjects: boolean;
    /** Cambiar el estado operativo Activo/Inactivo. */
    canChangeOperationalStatus: boolean;
    /** Eliminar una empresa que NO tiene historial de HH/HM (Escenario A del documento). */
    canDeleteWithoutHistory: boolean;
    /**
     * Eliminar una empresa CON historial de HH/HM (Escenario B del documento).
     *
     * Queda declarado para la Fase 2. En Fase 1 el backend rechaza siempre la eliminación
     * de empresas con historial, así que la interfaz mantiene el botón bloqueado incluso
     * para los perfiles que aquí aparecen habilitados.
     */
    canDeleteWithHistory: boolean;
}

/**
 * Resuelve los permisos del módulo para un perfil.
 *
 * @param role descripción del perfil, tal como viene en user.rols.description.
 */
export const getSubcontractPermissions = (role: string): SubcontractPermissions => {
    const isManager = role === MANAGER;
    const isDirector = role === DIRECTOR;
    const isWorksControl = role === WORKS_CONTROL;
    const isAdmin = role === ADMIN;
    const isViewer = role === VIEWER;
    // Administrador de Sistema: super-administrador con acceso a todo (ver profiles.ts).
    // El modulo de subcontratos es anterior a este rol, por eso se contempla aparte.
    const isSystemAdmin = role === ADMIN_SISTEMA;

    if (isSystemAdmin) {
        return {
            canView: true,
            canCreate: true,
            canEditBasicData: true,
            canEditSections: true,
            canEditProjects: true,
            canChangeOperationalStatus: true,
            canDeleteWithoutHistory: true,
            canDeleteWithHistory: true,
        };
    }

    return {
        canView: isManager || isDirector || isWorksControl || isAdmin || isViewer,
        canCreate: isManager || isDirector || isWorksControl || isAdmin,
        canEditBasicData: isManager || isDirector || isWorksControl || isAdmin,
        canEditSections: isManager || isDirector || isWorksControl,
        canEditProjects: isManager || isDirector || isWorksControl,
        canChangeOperationalStatus: isManager || isDirector || isWorksControl,
        canDeleteWithoutHistory: isManager || isDirector || isWorksControl || isAdmin,
        canDeleteWithHistory: isManager || isDirector,
    };
};

/**
 * Lee el perfil del usuario autenticado desde localStorage.
 * Devuelve cadena vacía si no hay sesión, con lo que todos los permisos quedan en false.
 */
export const getCurrentRole = (): string => {
    const stored = localStorage.getItem("user");
    if (!stored) {
        return "";
    }
    try {
        return JSON.parse(stored)?.rols?.description ?? "";
    } catch {
        return "";
    }
};
